import { useState } from 'react';

import {
  ArrowDownward as DownIcon,
  ArrowUpward as UpIcon,
  WarningAmberRounded,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { ExportMenu } from '@/components/export/ExportMenu';
import type { ResidentDto } from '@/lib/api/residents';
import {
  type VoiceHistoryDay,
  type VoiceHistoryDetail,
  type VoiceHistorySession,
  type VoiceHistorySummary,
  getResidentVoiceHistory,
  VOICE_SCORE_LABELS,
} from '@/lib/api/voice-history';
import {
  type VoiceScoreCard,
  type VoiceTodayDetail,
  getResidentVoiceToday,
} from '@/lib/api/voice-today';
import { PROGRAM_LABEL } from '@/lib/program-labels';

import { type PeriodRange, PeriodPickerBar } from '../PeriodPickerBar';
import { VoiceHistoryChart } from '../VoiceHistoryChart';

interface Props {
  resident: ResidentDto | null;
}

function defaultRange(): PeriodRange {
  // BGT TabPeriod 와 동일 — 기본 최근 2주(오늘 포함 14일)
  const to = dayjs().startOf('day');
  const from = to.subtract(13, 'day');
  return { from, to };
}

function formatScore(v: number | null): string {
  return v === null ? '—' : `${v}점`;
}

function DeltaBadge({ deltaPct, dropped }: { deltaPct: number | null; dropped: boolean }) {
  if (deltaPct === null) {
    return (
      <Typography variant="caption" color="text.disabled">
        비교 자료 없음
      </Typography>
    );
  }
  if (deltaPct === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        전일과 동일
      </Typography>
    );
  }
  const isDown = deltaPct < 0;
  const color = dropped ? 'error.main' : isDown ? 'warning.main' : 'success.main';
  const Icon = isDown ? DownIcon : UpIcon;
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      {dropped && <WarningAmberRounded sx={{ color: 'error.main', fontSize: 16 }} />}
      <Icon sx={{ color, fontSize: 16 }} />
      <Typography variant="caption" sx={{ color, fontWeight: 600 }}>
        {Math.abs(deltaPct)}%
      </Typography>
    </Stack>
  );
}

function ScoreCardItem({ card }: { card: VoiceScoreCard }) {
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Stack spacing={0.5}>
          <Typography variant="caption" color="text.secondary">
            {card.label}
            {card.status ? ` · ${card.status}` : ''}
          </Typography>
          <Typography variant="h5">{formatScore(card.todayScore)}</Typography>
          <DeltaBadge deltaPct={card.deltaPct} dropped={card.dropped} />
        </Stack>
      </CardContent>
    </Card>
  );
}

function VoiceSummaryCard({
  summary,
  from,
  to,
}: {
  summary: VoiceHistorySummary;
  from: string;
  to: string;
}) {
  // 활성일 단순 평균 — BGT HistorySummaryCard 의 testCount 가중과 다름
  const cells = [
    { label: '활성 일수', value: `${summary.activeDays}일` },
    { label: '총 세션', value: `${summary.sessionCount}` },
    { label: `${VOICE_SCORE_LABELS.hearingScore} 평균`, value: formatScore(summary.avgHearing) },
    {
      label: `${VOICE_SCORE_LABELS.cognitiveScore} 평균`,
      value: formatScore(summary.avgCognitive),
    },
    { label: `${VOICE_SCORE_LABELS.stressScore} 평균`, value: formatScore(summary.avgStress) },
  ];

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
          구간 요약 ({from} ~ {to})
        </Typography>
        <Grid container spacing={2}>
          {cells.map((c) => (
            <Grid size={{ xs: 6, sm: 4, md: 2.4 }} key={c.label}>
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">
                  {c.label}
                </Typography>
                <Typography variant="h6">{c.value}</Typography>
              </Stack>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );
}

function DailyTable({ days }: { days: VoiceHistoryDay[] }) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
          일자별 ({days.length}일)
        </Typography>
        <Box sx={{ overflowX: 'auto' }}>
          <Box
            component="table"
            sx={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '0.85rem',
              '& th, & td': {
                p: 0.75,
                borderBottom: 1,
                borderColor: 'divider',
                textAlign: 'right',
                whiteSpace: 'nowrap',
              },
              '& th:first-of-type, & td:first-of-type': { textAlign: 'left' },
              '& th': { fontWeight: 700, bgcolor: 'background.default' },
              '& tr.empty td': { color: 'text.disabled' },
            }}
          >
            <Box component="thead">
              <Box component="tr">
                <Box component="th">날짜</Box>
                <Box component="th">세션</Box>
                <Box component="th">{VOICE_SCORE_LABELS.hearingScore}</Box>
                <Box component="th">{VOICE_SCORE_LABELS.cognitiveScore}</Box>
                <Box component="th">{VOICE_SCORE_LABELS.stressScore}</Box>
              </Box>
            </Box>
            <Box component="tbody">
              {days.map((d) => (
                <Box component="tr" key={d.date} className={d.hasData ? '' : 'empty'}>
                  <Box component="td">{d.date}</Box>
                  <Box component="td">{d.hasData ? d.sessionCount : '—'}</Box>
                  <Box component="td">{d.hasData ? formatScore(d.hearingScore) : '—'}</Box>
                  <Box component="td">{d.hasData ? formatScore(d.cognitiveScore) : '—'}</Box>
                  <Box component="td">{d.hasData ? formatScore(d.stressScore) : '—'}</Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

function SessionsTable({ sessions }: { sessions: VoiceHistorySession[] }) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
          세션 상세 ({sessions.length}건)
        </Typography>
        {sessions.length === 0 ? (
          <Typography variant="body2" color="text.disabled">
            구간에 완료된 세션이 없습니다.
          </Typography>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Box
              component="table"
              sx={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '0.85rem',
                '& th, & td': {
                  p: 0.75,
                  borderBottom: 1,
                  borderColor: 'divider',
                  textAlign: 'right',
                  whiteSpace: 'nowrap',
                },
                '& th:first-of-type, & td:first-of-type': { textAlign: 'left' },
                '& th': { fontWeight: 700, bgcolor: 'background.default' },
              }}
            >
              <Box component="thead">
                <Box component="tr">
                  <Box component="th">날짜</Box>
                  <Box component="th">세션ID</Box>
                  <Box component="th">{VOICE_SCORE_LABELS.hearingScore}</Box>
                  <Box component="th">{VOICE_SCORE_LABELS.cognitiveScore}</Box>
                  <Box component="th">{VOICE_SCORE_LABELS.stressScore}</Box>
                </Box>
              </Box>
              <Box component="tbody">
                {sessions.map((s) => (
                  <Box component="tr" key={s.testId}>
                    <Box component="td">{s.date}</Box>
                    <Box component="td">{s.testId}</Box>
                    <Box component="td">
                      {formatScore(s.hearingScore)}
                      {s.hearingStatus ? ` · ${s.hearingStatus}` : ''}
                    </Box>
                    <Box component="td">
                      {formatScore(s.cognitiveScore)}
                      {s.cognitiveStatus ? ` · ${s.cognitiveStatus}` : ''}
                    </Box>
                    <Box component="td">
                      {formatScore(s.stressScore)}
                      {s.stressStatus ? ` · ${s.stressStatus}` : ''}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * 의뢰서 5-2 탭3 보이스웰니스
 */
export function TabVoice({ resident }: Props) {
  const [range, setRange] = useState<PeriodRange>(defaultRange);
  const from = range.from.format('YYYY-MM-DD');
  const to = range.to.format('YYYY-MM-DD');

  const todayQuery = useQuery<VoiceTodayDetail>({
    queryKey: ['residents', 'voice-today', resident?.id],
    queryFn: () => getResidentVoiceToday(resident!.id),
    enabled: !!resident,
    staleTime: 30 * 1000,
  });

  const historyQuery = useQuery<VoiceHistoryDetail>({
    queryKey: ['residents', 'voice-history', resident?.id, from, to],
    queryFn: () => getResidentVoiceHistory(resident!.id, from, to),
    enabled: !!resident,
    staleTime: 60 * 1000,
  });

  if (!resident) return null;

  const exportParams = { from, to };
  const history = historyQuery.data;
  // 매핑 권위: voice/today·history (voicecheck) > 상세 DTO (캐시·구버전 응답에 필드 없을 수 있음)
  const mappedVoiceUserId =
    todayQuery.data?.voiceUserId ?? history?.voiceUserId ?? resident.voiceUserId ?? null;

  return (
    <Stack spacing={2}>
      {/* 최근 평가 (오늘 KST) */}
      {todayQuery.isLoading ? (
        <Stack spacing={2}>
          <Skeleton variant="rounded" height={80} />
          <Grid container spacing={2}>
            {[0, 1, 2].map((i) => (
              <Grid size={{ xs: 12, sm: 4 }} key={i}>
                <Skeleton variant="rounded" height={110} />
              </Grid>
            ))}
          </Grid>
        </Stack>
      ) : todayQuery.data ? (
        <Card variant="outlined">
          <CardContent>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 2 }}
            >
              <Typography variant="subtitle1" fontWeight={700}>
                최근 평가
              </Typography>
              <Stack direction="row" spacing={1}>
                <Chip size="small" label={todayQuery.data.summary.date} variant="outlined" />
                <Chip
                  size="small"
                  label={todayQuery.data.summary.hasData ? '완료 세션' : '데이터 없음'}
                  color={todayQuery.data.summary.hasData ? 'primary' : 'default'}
                  variant="outlined"
                />
              </Stack>
            </Stack>

            {!todayQuery.data.summary.hasData && (
              <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
                {mappedVoiceUserId == null
                  ? '보이스웰니스 계정이 연결되지 않았습니다. 입소자 수정에서 계정을 연결하세요.'
                  : '해당 일자에 완료된 보이스웰니스 세션이 없습니다.'}
              </Alert>
            )}

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              소리찾기 · 두뇌체조 · 마음온도 점수를 각각 표시합니다.
            </Typography>
            <Grid container spacing={2}>
              {todayQuery.data.scores.map((card) => (
                <Grid size={{ xs: 12, sm: 4 }} key={card.type}>
                  <ScoreCardItem card={card} />
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      ) : null}

      {/* 기간 선택 + 리포트 2종 (점수 추이 / 세션 상세) */}
      <Card variant="outlined">
        <CardContent>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ sm: 'flex-start' }}
            spacing={1.5}
            sx={{ mb: 1.5 }}
          >
            <Typography variant="subtitle1" fontWeight={700}>
              기간 데이터
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <ExportMenu
                label="점수 추이 리포트"
                baseUrl={`/residents/${resident.id}/voice/history/export`}
                params={exportParams}
                fallbackBaseName={`${PROGRAM_LABEL.voice.short}기간점수_${from}_${to}`}
                disabled={!history || historyQuery.isLoading}
              />
              <ExportMenu
                label="세션 상세 리포트"
                baseUrl={`/residents/${resident.id}/voice/sessions/export`}
                params={exportParams}
                fallbackBaseName={`${PROGRAM_LABEL.voice.short}세션상세_${from}_${to}`}
                disabled={!history || historyQuery.isLoading}
              />
            </Stack>
          </Stack>
          <PeriodPickerBar value={range} onChange={setRange} />
        </CardContent>
      </Card>

      {historyQuery.isError && (
        <Alert severity="error">
          {(historyQuery.error as Error)?.message ?? '기간 데이터를 불러오지 못했습니다'}
        </Alert>
      )}

      {historyQuery.isLoading ? (
        <Stack alignItems="center" sx={{ py: 4 }}>
          <CircularProgress size={24} />
        </Stack>
      ) : history ? (
        <>
          <VoiceSummaryCard summary={history.summary} from={history.from} to={history.to} />
          <VoiceHistoryChart days={history.days} />
          <DailyTable days={history.days} />
          <SessionsTable sessions={history.sessions} />
        </>
      ) : null}
    </Stack>
  );
}
